import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { faSortUp, faSortDown, faX, faExclamationTriangle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loader, Spinner } from '../Loader/Loader';
import { Msg200or201, Msg204, Msg400, Msg401, Msg403, Msg500 } from '../Messages/Messages';
import useCommonResponseHandler from '../CommenResponseAndErrorHandlers/CommenResponseHandler';
import Search from '../Components/Search';
import Pagination, { usePagination } from '../Components/Pagination';
import { decryptData } from '../../utils/storageCrypto';
import { BanknotesIcon } from '@heroicons/react/24/outline';

const VendorPaymentList = () => {
  const navigate = useNavigate();
  const [productCategories, setProductCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchChips, setSearchChips] = useState(() => {
    const saved = sessionStorage.getItem('paymentSearchChips');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);

  const [salesOrders, setSalesOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const url = process.env.REACT_APP_FORE_BRICKBUCKET;
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const RETRY_LIMIT = 3;
  const [retryCount, setRetryCount] = useState(0);
  const { 
    showPopup, popupMessage, popupType, retryFunction, handleGetResponse, handleSaveResponse, setShowPopup,
    isSaving, setRetryFunction, handleDeleteResponse, setIsSaving, setPopupMessage,
    setPopupType 
  } = useCommonResponseHandler();
  const [SOItemQty, setSOItemQty] = useState(false);
  const [ReceivableQty, setReceivableQty] = useState(false);
  const [PendingQty, setPendingQty] = useState(false);
  const [createPOButtonEnabled, setCreatePOButtonEnabled] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [productCategoryId, setProductCategoryId] = useState(null);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showSOItemStatusPopup, setShowSOItemStatusPopup] = useState(false);
  const [orderItemsDataOfSO, setOrderItemsDataOfSO] = useState([]);
  const [roleId, setRoleId] = useState(null);
  const [filteredSO, setFilteredSO] = useState([]);
  const searchTypes = ['SO Number', 'Customers', 'Item Category', 'SO Status'];
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);

  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const paymentSearchTypes = ['Payment no', 'Reference Number', 'Vendor Name', 'Bill no', 'Mode'];

  const hasActiveSearch = searchChips.length > 0 || searchQuery.trim() !== '';
  const paymentsToDisplay = hasActiveSearch ? filteredPayments : payments;

  const {
    currentItems: currentPayments,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
    resetToFirstPage,
  } = usePagination(paymentsToDisplay, 10);

  const sortArray = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    // Function to get nested value using key
    const getNestedValue = (item, key) => {
      return key.split('.').reduce((value, keyPart) => {
        return value ? value[keyPart] : undefined;
      }, item);
    };

    const sortedArray = [...paymentsToDisplay].sort((a, b) => {
      const aValue = getNestedValue(a, key) || '';
      const bValue = getNestedValue(b, key) || '';

      // Convert to string for comparison and handle case insensitivity
      const aString = aValue.toString().toLowerCase();
      const bString = bValue.toString().toLowerCase();

      if (aString < bString) {
        return direction === 'ascending' ? -1 : 1;
      }
      if (aString > bString) {
        return direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    if (hasActiveSearch) {
      setFilteredPayments(sortedArray);
    } else {
      setPayments(sortedArray);
    }

    resetToFirstPage();
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
    }
    return null;
  };

  useEffect(() => {
    handleSearch();
  }, [salesOrders]);

  const handleSearch = (query, type) => {
    if (!query || !type) {
      setFilteredSO(salesOrders);
      return;
    }
  
    const filtered = salesOrders.filter((salesOrder) => {
      const lowercaseQuery = query.toLowerCase();
      switch (type) {
        case 'SO Number':
          return salesOrder.salesorder_number?.toLowerCase().includes(lowercaseQuery);
        case 'Customers':
          return salesOrder?.customer_id?.customer_name?.toLowerCase().includes(lowercaseQuery);
        case 'Item Category':
          return salesOrder.product_category_id?.product_category_name?.toLowerCase().includes(lowercaseQuery);
        case 'SO Status':
          return salesOrder.salesorder_status?.toLowerCase().includes(lowercaseQuery);
        default:
          return false;
      }
    });

    setFilteredSO(filtered);
  };

const handlePaymentSearch = (query, type) => {
  if (!type && query === '') {
    setFilteredPayments(payments);
    return;
  }

  const filtered = payments.filter((payment) => {
    const lowercaseQuery = (query || '').toLowerCase();
    switch (type) {
      case 'Payment no':
        return (payment?.payment_number || '').toLowerCase().includes(lowercaseQuery);
      case 'Reference Number':
        return (payment?.reference_number || '').toLowerCase().includes(lowercaseQuery);
      case 'Vendor Name':
        return (payment?.vendor_id?.company_name || '').toLowerCase().includes(lowercaseQuery);
      case 'Bill no':
        return (payment?.bill_number || '').toLowerCase().includes(lowercaseQuery);
      case 'Mode':
        return (payment?.payment_mode || '').toLowerCase().includes(lowercaseQuery);
      default:
        return false;
    }
  });

  setFilteredPayments(filtered);
  resetToFirstPage();
};


  const handleRemoveSearchChip = (chipToRemove) => {
    const updated = searchChips.filter(
      (chip) => !(chip.query === chipToRemove.query && chip.type === chipToRemove.type)
    );
    setSearchChips(updated);
    sessionStorage.setItem('paymentSearchChips', JSON.stringify(updated));

    if (updated.length === 0) {
      setFilteredPayments(payments);
      setSearchQuery('');
      setSearchType(''); 
    } else {
      const last = updated[updated.length - 1];
      handlePaymentSearch(last.query, last.type);
    }
  };

  const handleAddSearchChip = (query, type) => {
    if (!type) return;

    const actualQuery = query.trim(); 

    setSearchChips((prev) => {
      const exists = prev.some((chip) => chip.query === actualQuery && chip.type === type);
      if (exists) return prev;

      const updated = [...prev, { query: actualQuery, type }]; 
      sessionStorage.setItem('paymentSearchChips', JSON.stringify(updated)); 
      return updated;
    });

    setSearchQuery(actualQuery); 
    setSearchType(type);        

    handlePaymentSearch(actualQuery, type);
  };

  const handleChipClick = (chip) => {
    setSearchType(chip.type);
    setSearchQuery(chip.query);
    handlePaymentSearch(chip.query, chip.type);
  };

  async function getAllPayments() {
    const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);

    try {
      const response = await fetch(`${fetchUrl}vendor-payments/get-all-vendor-payments`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        },
      });
  
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('payments:', data.data);
          setPayments(data.data);
          setFilteredPayments(data.data);
          setIsLoading(false);
        } else {
          console.error('Error:', data.message);
          alert(`Failed to fetch payments: ${data.message}`);
          setIsLoading(false);
        }
      } else {
        console.error('HTTP Error:', response.status);
        alert(`HTTP Error: ${response.status}`);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      alert('Failed to fetch payments due to a network error');
      setIsLoading(false);
    }
  }
  
  useEffect(() => {
    getAllPayments();
  }, [fetchUrl]);

  const handleCreate = () => {
    navigate("/bb/app/vendorpayments/newvendorpayment");
  };

  useEffect(() => {
    fetchRoleId();
  }, []);

  const fetchRoleId = () => {
    const enroleId = sessionStorage.getItem('roleId');
    const sroleId = decryptData(enroleId);
    setRoleId(sroleId);
  };

  const salesOrdersToDisplay = salesOrders && roleId === 6 
    ? salesOrders.filter(salesorder => (salesorder.product_category_id?.product_category_name.toLowerCase()) === 'tiles')
    : salesOrders || [];
  console.log(salesOrdersToDisplay);

  const closePopup = () => {
    console.log("closePopup called");
    setShowPopup(false);
  };
  
  const Retry = async() => {
    console.log("Retry called");
    setTimeout(async ()=>{
      if(retryFunction){
        await retryFunction ();
      }
      setIsLoading(false)
    },1000)    
    setShowPopup(false);
  };
  
  const BackToLoginPage = () => {
    console.log("Back To Login Page");
    setShowPopup(false);
    localStorage.removeItem('LoginData');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('appId');
    navigate('/bb');
  };

 function formatSalesOrderDate(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'N/A'; // Invalid date fallback
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

  function calculateAgingDays(dateStr) {
    const salesOrderDate = new Date(dateStr);
    const currentDate = new Date();
    const diffInMs = currentDate.getTime() - salesOrderDate.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 3600 * 24));
    return diffInDays;
  }
  
  const handleStayHere = () => {
    setShowCancelPopup(false);
    setShowSOItemStatusPopup(false);
  }

  const handleNavigateToPaymentDetails = (payment) => {
    navigate("/bb/app/vendorpayments/vendorpaymentdetails", {state:{PaymentData: payment}});
  };

  if (isLoading) {
    return <Loader />;
  }
  
  return (
    <div className="bg-white flex flex-row w-full justify-left h-full overflow-y-hidden">
      <div className="flex flex-row w-full">
        <div className="w-full flex-1">
          <div className="flex flex-col w-full justify-center items-center">
            <div className="w-full h-full flex flex-col justify-between">
              <div className="w-full h-16 border-b flex items-center">
                <div className="flex w-full justify-between px-4 items-center">
                  {selectedCategories.length > 0 ? (
                    <>
                      <div className="flex space-x-2">
                        <button
                          // onClick={handleDelete}
                          className="bg-gray-100 px-2 py-1 shadow-sm text-sm border rounded-md"
                        >
                          Delete
                        </button>
                        <button
                          // onClick={handleCreatePurchaseOrder}  // Ensure this function is defined
                          className="bg-gray-100 px-2 py-1 shadow-sm text-sm border rounded-md"
                          // disabled={!createPOButtonEnabled}
                        >
                          Create Purchase Order
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='space-x-10 flex flex-row'>
                        <h1 className="text-lg font-bold whitespace-nowrap">All Vendor Payments</h1>
                        {payments && payments.length > 0 && (
                          <>
                        <Search
                          searchQuery={searchQuery}
                          setSearchQuery={setSearchQuery}
                          searchType={searchType}
                          setSearchType={setSearchType}
                          searchTypes={paymentSearchTypes}
                          onSearch={handlePaymentSearch}
                          onSearchSubmit={handleAddSearchChip}
                          placeholder="Search..."
                        />
                       
                        {searchChips.length > 0 && (
                          <div className="w-full flex flex-cols gap-3 items-center mt-2 px-4">
                            {searchChips.map((chip, index) => {
                              const isActive = chip.type === searchType;

                              const chipClasses = `flex items-center max-w-xs text-sm px-3 py-1 rounded-full shadow-sm transition-all duration-150 ${
                                isActive
                                  ? 'bg-red-500 text-white'
                                  : 'bg-gray-100 text-gray-800'
                              }`;

                              return (
                                <div
                                  key={`${chip.type}-${index}`}
                                  className={chipClasses}
                                  style={{ whiteSpace: 'nowrap' }}
                                  onClick={() => handleChipClick(chip)}
                                >
                                  <span className="whitespace-nowrap max-w-[130px]">
                                    {chip.query ? chip.query : chip.type}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveSearchChip(chip);
                                    }}
                                    className={`ml-2 text-base font-bold ${
                                      isActive ? 'text-white hover:text-gray-200' : 'text-gray-500 hover:text-red-500'
                                    }`}
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        </>
                         )}
                      </div>
                      <button
                        onClick={handleCreate}
                        className="bg-[#F7525A] text-white z-20 px-2 py-1 rounded "
                       title="Create Vendor Payment">
                        + New
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Top Pagination Info */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={10}
                showInfo={true}
                hideControls={true}
                itemName="vendor payments"
              />

              <div className="w-full h-full bg-white rounded-xs overflow-hidden">
                <div className="overflow-y-auto h-[80vh]">
                  {payments.length > 0 ? (
                    <table className="w-full mb-5">
                      <thead className="sticky top-0 bg-[#fafafc] text-[#6c7184] border-b ">
                        <tr className='border-b'>
                          <th className="text-xs py-3 text-center">
                            <input type="checkbox" className="mr-2" />
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('payment_date')}>
                            Date {getSortIcon('payment_date') && <FontAwesomeIcon icon={getSortIcon('payment_date')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('payment_number')}>
                            Payment No {getSortIcon('payment_number') && <FontAwesomeIcon icon={getSortIcon('payment_number')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('reference_number')}>
                            Reference Number {getSortIcon('reference_number') && <FontAwesomeIcon icon={getSortIcon('reference_number')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('customer_id.customer_name')}>
                            Vendor Name {getSortIcon('customer_id.customer_name') && <FontAwesomeIcon icon={getSortIcon('customer_id.customer_name')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('bill_number')}>
                            Bill {getSortIcon('bill_number') && <FontAwesomeIcon icon={getSortIcon('bill_number')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('payment_mode')}>
                            Mode {getSortIcon('payment_mode') && <FontAwesomeIcon icon={getSortIcon('payment_mode')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('status')}>
                            Status {getSortIcon('status') && <FontAwesomeIcon icon={getSortIcon('status')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('amount')}>
                            Amount {getSortIcon('amount') && <FontAwesomeIcon icon={getSortIcon('amount')} />}
                          </th>
                          <th className="text-xs py-3 text-center cursor-pointer" onClick={() => sortArray('unused_amount')}>
                            Unused Amount {getSortIcon('unused_amount') && <FontAwesomeIcon icon={getSortIcon('unused_amount')} />}
                          </th>
                        </tr>
                      </thead>
                                        <tbody className="border-b border-t">
                        {currentPayments.length > 0 ? (
                          currentPayments.map((payment) => (
                            <tr key={payment._id} className="text-sm border-b hover:bg-gray-50">
                              <td className="py-2 text-center">
                                <input type="checkbox" className="mr-2" />
                              </td>
                              <td className="py-2 text-center">{formatSalesOrderDate(payment?.payment_date)}</td>
                            <td 
  className="py-2 text-center text-[#408dfb] cursor-pointer"
  onClick={() => handleNavigateToPaymentDetails(payment)}
>
  {payment?.payment_number || 'N/A'}
</td>

                              <td className="py-2 text-center">{payment?.reference_number || 'N/A'}</td>
                              <td className="py-2 text-center">{payment?.vendor_id?.company_name || 'N/A'}</td>
                              <td className="py-2 text-center">{payment?.bills.map(bill => bill.bill_number || '')}</td>
                              <td className="py-2 text-center">{payment?.payment_mode || 'N/A'}</td>
                              <td className="py-2 text-center">{payment?.status || 'N/A'}</td>
                              <td className="py-2 text-center">₹{payment?.amount?.toFixed(2) || 0}</td>
                              <td className="py-2 text-center">₹{payment?.unused_amount?.toFixed(2) || 0}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="p-12 text-center text-gray-500">
                              <div className="flex flex-col items-center">
                                <FontAwesomeIcon
                                  icon={faSearch}
                                  className="text-4xl text-gray-300 mb-4"
                                />
                                <p className="text-lg font-medium mb-2">
                                  No results found.
                                </p>
                                <p className="text-sm">
                                  Try adjusting your search criteria.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <>
                      {/* <div className="m-4 p-4 text-center border border-grey-500 text-gray-500">
                        No Payments Found.
                      </div> */}
                      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 mx-4 mt-4">
                  <div className="text-center text-gray-500">
                    <div className="mb-4">
                      <BanknotesIcon  className="mx-auto h-12 w-12 text-gray-300" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No Vendor Payments Found</h3>
                    <p className="text-sm"> Vendor payments will appear here once they are created.</p>
                  </div>
                      <div className="flex justify-center items-center mt-4">
                        <button className="bg-[#F7525A] text-white px-2 py-1 rounded-md" onClick={handleCreate}>Create</button>
                      </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Bottom Pagination */}
              <div className="sticky bottom-0 bg-white shadow-md z-10 px-4 py-2">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  onPreviousPage={handlePreviousPage}
                  onNextPage={handleNextPage}
                  showInfo={false}
                  itemName="vendor payments"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    

    {showCancelPopup && (
      <div className="fixed inset-0 flex items-top justify-center bg-gray-800 bg-opacity-50 ">
        <div className="bg-white rounded-b-md shadow-md w-[550px] h-[200px] flex flex-col" style={{ maxWidth: '550px', maxHeight: '200px' }}>
          <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-14 px-6'>
            <h2 className="text-md font-semibold">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl text-yellow-500 mr-4 " />
              Unable to select?
            </h2>
            <button onClick={handleStayHere} className="text-white px-1 py-1 rounded-md ">   
              <FontAwesomeIcon icon={faX} className="text-xs text-[#212529] cursor-pointer " />
            </button>
          </div>
          <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
          <div className='pt-4 pl-4 '>
            <div className="w-full items-center p-5">
              <label htmlFor="Other_details" className="block text-sm font-regular text-[#212529] ">
                Kindly select Sales Order with same Item Category.
              </label>
            </div>  
          </div>
          <div className="mt-auto w-full">
            <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
            <div className="text-sm flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
              <button className="px-2 py-1 bg-[#408dfb] border-[#f7525a] text-white rounded-md" onClick={handleStayHere}>
                Okay
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showPopup && (
      <div>
        {popupType === '400' && <Msg400 message={popupMessage} closePopup={closePopup} />}
        {popupType === '401' && <Msg401 message={popupMessage} closePopup={BackToLoginPage} />}
        {popupType === '403' && <Msg403 message={popupMessage} closePopup={BackToLoginPage} />}
        {popupType === '500' && <Msg500 message={popupMessage} closePopup={Retry} cancelPopup={closePopup} />}
      </div>
    )}
    </div>
  );
};


export default VendorPaymentList;

                             